// Core Web Vitals optimization utilities

export class PerformanceOptimizer {
  // Bump this when you change SW caching behavior to force stale-cache cleanup for returning visitors
  private static readonly SW_CACHE_VERSION = 'v7';
  // Lazy loading for images
  static setupImageLazyLoading(): void {
    // Use intersection observer for better performance
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            imageObserver.unobserve(img);
          }
        }
      });
    });

    // Observe all images with data-src attribute
    document.querySelectorAll('img[data-src]').forEach((img) => {
      imageObserver.observe(img);
    });
  }

  // Preload critical resources - fonts are loaded via CSS/Tailwind
  static preloadCriticalResources(): void {
    // No custom fonts to preload - using system fonts via Tailwind
    // This prevents 404 errors for non-existent font files
  }

  // Optimize Cumulative Layout Shift (CLS)
  static preventLayoutShift(): void {
    // Add aspect ratio containers for images
    const images = document.querySelectorAll('img:not([width]):not([height])');
    images.forEach((img) => {
      const container = document.createElement('div');
      container.style.aspectRatio = '16/9'; // Default aspect ratio
      container.style.overflow = 'hidden';
      
      if (img.parentNode) {
        img.parentNode.insertBefore(container, img);
        container.appendChild(img);
      }
    });
  }

  // Resource hints for better loading
  // NOTE: preconnects for fonts/supabase already exist in index.html.
  // Adding them again created duplicate/unused preconnect warnings, so this is a no-op.
  static addResourceHints(): void {
    return;
  }


  // Enhanced caching strategy
  static setupCaching(): void {
    // Register service worker for caching (only in production)
    if ('serviceWorker' in navigator && 'caches' in window && window.location.hostname !== 'localhost') {
      // If a returning visitor has an older cache version, clear caches + unregister SW once, then reload.
      // This prevents being stuck on route-level Suspense loaders due to stale hashed chunks.
      const versionKey = '__app_sw_cache_version';
      const previousVersion = window.localStorage.getItem(versionKey);

      if (previousVersion && previousVersion !== PerformanceOptimizer.SW_CACHE_VERSION) {
        window.localStorage.setItem(versionKey, PerformanceOptimizer.SW_CACHE_VERSION);
        Promise.all([
          navigator.serviceWorker.getRegistrations().then((regs) => Promise.all(regs.map((r) => r.unregister()))),
          caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k)))),
        ]).finally(() => {
          // Reload to fetch the current HTML + assets
          window.location.reload();
        });
        return;
      }

      if (!previousVersion) {
        window.localStorage.setItem(versionKey, PerformanceOptimizer.SW_CACHE_VERSION);
      }

      // First, try to unregister any stale service workers
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        // If there are old service workers, update them.
        // update() can reject (offline, throttled CDN) — swallow it so it doesn't
        // surface as a console error in audits.
        registrations.forEach((registration) => {
          registration.update().catch(() => {});
        });
      }).catch(() => {});

      navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' })
        .then((registration) => {
          // Check for updates immediately
          registration.update().catch(() => {});

          // If there's a waiting worker, activate it now
          if (registration.waiting) {
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          }

          // When a new worker is found, activate it as soon as it's installed
          registration.addEventListener('updatefound', () => {
            const installing = registration.installing;
            if (!installing) return;
            installing.addEventListener('statechange', () => {
              if (installing.state === 'installed' && registration.waiting) {
                registration.waiting.postMessage({ type: 'SKIP_WAITING' });
              }
            });
          });

          // NOTE: we intentionally do NOT reload on 'controllerchange' here.
          // Reloading when the SW takes control doubles the page load for every
          // first-time visitor (and every CrUX/PageSpeed measurement), which was
          // the main driver of poor field-data reports. Stale-chunk recovery is
          // already handled by runtimeRecovery.ts on actual chunk-load errors.
        })
        .catch((error) => {
          console.warn('Service worker registration failed:', error);
        });
    }
  }

  private static async cacheStaticAssets(): Promise<void> {
    // Removed problematic caching that was causing errors
    return;
  }

  // Image compression utilities
  static getOptimizedImageUrl(url: string, width?: number, height?: number): string {
    if (url.includes('unsplash.com')) {
      const params = new URLSearchParams();
      if (width) params.set('w', width.toString());
      if (height) params.set('h', height.toString());
      params.set('fit', 'crop');
      params.set('auto', 'format,compress');
      params.set('q', '80');
      
      return `${url.split('?')[0]}?${params.toString()}`;
    }
    return url;
  }

  // Measure Core Web Vitals
  static measureWebVitals(): void {
    // This would integrate with web-vitals library in production
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if ('value' in entry) {
          console.log(`${entry.name}: ${(entry as any).value}`);
        } else {
          console.log(`${entry.name}: ${entry.duration || 'N/A'}`);
        }
      }
    });

    try {
      observer.observe({ entryTypes: ['measure', 'navigation', 'paint', 'largest-contentful-paint'] });
    } catch (e) {
      // Fallback for browsers that don't support all entry types
      observer.observe({ entryTypes: ['measure', 'navigation'] });
    }
  }

  // Initialize all optimizations
  static initialize(): void {
    // Run when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.preloadCriticalResources();
        this.addResourceHints();
        this.setupCaching();
        this.setupImageLazyLoading();
        this.preventLayoutShift();
        this.measureWebVitals();
      });
    } else {
      this.preloadCriticalResources();
      this.addResourceHints();
      this.setupCaching();
      this.setupImageLazyLoading();
      this.preventLayoutShift();
      this.measureWebVitals();
    }
  }
}

// Optimized image component
export interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  loading?: 'lazy' | 'eager';
  priority?: boolean;
}

export const getOptimizedImageProps = ({
  src,
  alt,
  width,
  height,
  loading = 'lazy',
  priority = false
}: OptimizedImageProps) => {
  const props: any = {
    alt,
    loading: priority ? 'eager' : loading,
    decoding: 'async',
  };

  // Add dimensions for CLS prevention
  if (width && height) {
    props.width = width;
    props.height = height;
  }

  // Get optimized image URL with compression
  const optimizedSrc = PerformanceOptimizer.getOptimizedImageUrl(src, width, height);

  // Use data-src for lazy loading, direct src for priority images
  if (priority || loading === 'eager') {
    props.src = optimizedSrc;
  } else {
    props['data-src'] = optimizedSrc;
    props.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMSIgaGVpZ2h0PSIxIiB2aWV3Qm94PSIwIDAgMSAxIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9InRyYW5zcGFyZW50Ii8+PC9zdmc+';
  }

  return props;
};